// 应用外壳：指挥台风格深色布局，桌面侧栏 / 移动抽屉，顶栏含语言与时区切换
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Avatar, Drawer, Dropdown, Grid, Layout, Menu, Select, Space, Tag, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import {
  AlertOutlined, AuditOutlined, BarChartOutlined, CloudServerOutlined,
  GlobalOutlined, LogoutOutlined, MobileOutlined, PartitionOutlined,
  ProfileOutlined, TeamOutlined, ThunderboltFilled, UserOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { LANG_OPTIONS } from '../i18n';
import { TZ_PRESETS, getViewTz, setViewTz } from '../utils/timefmt';
import { useViewTz } from '../utils/timefmt';

const { Header, Sider, Content } = Layout;

export function Shell({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const { user, logout, isSuper } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const screens = Grid.useBreakpoint();
  const narrow = !screens.md;
  const [drawer, setDrawer] = useState(false);
  const tz = useViewTz();
  const [tzTick, setTick] = useState(0);
  void tzTick;

  const items: MenuProps['items'] = useMemo(() => {
    const base: Required<MenuProps>['items'][number][] = [
      { key: '/dashboard', icon: <BarChartOutlined />, label: t('nav.dashboard') },
      { key: '/events', icon: <AlertOutlined />, label: t('nav.events') },
      { key: '/services', icon: <CloudServerOutlined />, label: t('nav.services') },
      { key: '/customers', icon: <PartitionOutlined />, label: t('nav.customers') },
      { key: '/categories', icon: <ProfileOutlined />, label: t('nav.categories') },
    ];
    if (isSuper) {
      base.push({ key: '/users', icon: <TeamOutlined />, label: t('nav.users') });
      base.push({ key: '/audit', icon: <AuditOutlined />, label: t('nav.audit') });
    }
    base.push({ key: '/settings', icon: <GlobalOutlined />, label: t('nav.settings') });
    base.push({ key: '/m', icon: <MobileOutlined />, label: t('nav.mobileHub') });
    return base;
  }, [t, isSuper]);

  const selected = useMemo(() => {
    const hit = ['/dashboard', '/events', '/services', '/customers', '/categories', '/users', '/audit', '/settings', '/m']
      .filter((p) => loc.pathname === p || loc.pathname.startsWith(p + '/'));
    return hit.length ? [hit.sort((a, b) => b.length - a.length)[0] as string] : ['/dashboard'];
  }, [loc.pathname]);

  const onMenu: MenuProps['onClick'] = ({ key }) => {
    nav(key);
    setDrawer(false);
  };

  const roleLabel = user ? t(`users.roles.${user.role}`, { defaultValue: user.role }) : '';

  const sidebar = (
    <Menu
      theme="dark"
      mode="inline"
      items={items}
      selectedKeys={selected}
      onClick={onMenu}
      style={{ background: 'transparent', borderInlineEnd: 0 }}
    />
  );

  return (
    <Layout style={{ minHeight: '100vh', background: 'transparent' }}>
      <div className="escalation-strip" />
      {!narrow && (
        <Sider width={216} theme="light" style={{ background: 'rgba(16,26,46,.72)', backdropFilter: 'blur(6px)', borderInlineEnd: '1px solid #22314f' }}>
          <div style={{ padding: '18px 16px 10px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <ThunderboltFilled style={{ fontSize: 22, color: '#f59e0b' }} />
            <div>
              <div style={{ fontWeight: 800, letterSpacing: 1, fontSize: 16, color: '#e8eefc' }}>
                {t('brand.name')}
              </div>
              <div style={{ fontSize: 11, color: '#93a4c4' }}>{t('brand.full')}</div>
            </div>
          </div>
          {sidebar}
        </Sider>
      )}
      {narrow && (
        <Drawer placement="left" width={230} open={drawer} onClose={() => setDrawer(false)}
          closable={false} styles={{ body: { background: '#101a2e', padding: 8 } }}>
          {sidebar}
        </Drawer>
      )}
      <Layout style={{ background: 'transparent' }}>
        <Header style={{
          background: 'rgba(11,18,32,.85)', backdropFilter: 'blur(8px)',
          borderBottom: '1px solid #22314f', padding: '0 16px', position: 'sticky', top: 0, zIndex: 20,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, height: 56, lineHeight: '56px',
        }}>
          <Space size={12}>
            {narrow && <a onClick={() => setDrawer(true)} style={{ color: '#e8eefc', fontSize: 18 }}><ProfileOutlined /></a>}
            <span style={{ fontWeight: 600, color: '#e8eefc' }}>
              {narrow ? t('brand.name') : t('brand.tagline')}
            </span>
          </Space>
          <Space size={8} wrap={false}>
            <Select
              size="small"
              variant="borderless"
              value={tz}
              style={{ minWidth: 130, color: '#93a4c4' }}
              popupMatchSelectWidth={240}
              onChange={(v) => { setViewTz(v); setTick((x) => x + 1); }}
              options={TZ_PRESETS.map((z) => ({
                value: z,
                label: (
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                    {z} <span style={{ opacity: .55 }}>{tzAbbrev(z)}</span>
                  </span>
                ),
              }))}
            />
            <Select
              size="small"
              variant="borderless"
              value={i18n.language}
              style={{ minWidth: 92 }}
              onChange={(v) => void i18n.changeLanguage(v)}
              options={LANG_OPTIONS}
            />
            <Dropdown
              menu={{
                items: [
                  { key: 'role', disabled: true, label: <Tag color="blue">{roleLabel}</Tag> },
                  { type: 'divider' },
                  { key: 'logout', icon: <LogoutOutlined />, label: t('common.logout'), onClick: () => { logout(); nav('/login'); } },
                ],
              }}
            >
              <Tooltip title={user?.name}>
                <Avatar style={{ cursor: 'pointer', background: '#3b82f6' }} icon={<UserOutlined />}>
                  {user?.name?.slice(0, 1)}
                </Avatar>
              </Tooltip>
            </Dropdown>
          </Space>
        </Header>
        <Content style={{ padding: narrow ? 12 : 20, overflow: 'auto' }}>
          <div className="page-shell">{children}</div>
        </Content>
      </Layout>
    </Layout>
  );
}

/** 时区当前 UTC 偏移缩写，如 UTC+8 */
function tzAbbrev(tz: string): string {
  try {
    const off = -new Date().getTimezoneOffset(); // 仅本地近似；选择器里用固定计算
    const d = new Date();
    const fmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' });
    const part = fmt.formatToParts(d).find((p) => p.type === 'timeZoneName')?.value ?? '';
    void off;
    return part.replace('GMT', 'UTC');
  } catch {
    return '';
  }
}
